/**
 * Helpers for the user's Alerts page, which lists the alert (new item) and
 * notification (update) emails a user was sent (GET /api/users/{id}/sent-emails).
 */

import { getDatasetLabel, getEmailChanges } from 'utils/adminEmailUtils';

// Dataset model class -> dataConfig key, for opening the record details modal.
// Only classes whose dataConfig details endpoint loads that same model: the endpoint looks the
// record up by id, so a BIS class mapped to an Open Data endpoint opens another record or a 404.
export const DATA_SET_BY_MODEL_CLASS = {
	// 311 Complaints
	'App\\Models\\TOOComplaint': 'complaints311',
	'TOOComplaint': 'complaints311',

	// DOB Complaints
	'App\\Models\\OpenData\\DOB\\OdDobComplaint': 'dobComplaints',
	'OdDobComplaint': 'dobComplaints',

	// DOB Violations
	'App\\Models\\OpenData\\DOB\\DOBViolation': 'dobViolations',
	'DOBViolation': 'dobViolations',

	// DOB Safety Violations
	'App\\Models\\OpenData\\DOB\\DOBSafetyViolation': 'dobSafetyViolations',
	'DOBSafetyViolation': 'dobSafetyViolations',

	// ECB Violations
	'App\\Models\\OpenData\\ECB\\ECBViolation': 'ecbViolations',
	'ECBViolation': 'ecbViolations',

	// ECB Ticket Finder Hearing
	'App\\Models\\OpenData\\ECB\\EcbTicketFinderHearing': 'ecbTicketFinderHearings',
	'EcbTicketFinderHearing': 'ecbTicketFinderHearings',

	// Job Applications
	'App\\Models\\OpenData\\DOB\\Now\\Build\\ODDobNowBuildJobApplicationFiling': 'dobJobApplications',
	'ODDobNowBuildJobApplicationFiling': 'dobJobApplications',
	'JobApplication': 'jobApplications',

	// Permits
	'App\\Models\\OpenData\\DOB\\Now\\Build\\ODDobNowBuildApprovedPermit': 'approvedPermits',
	'ODDobNowBuildApprovedPermit': 'approvedPermits',
	'App\\Models\\OpenData\\DOB\\ODDobPermitIssuance': 'dobPermitIssuances',
	'ODDobPermitIssuance': 'dobPermitIssuances',

	// Certificates of Occupancy
	'App\\Models\\OpenData\\DOB\\ODDobCO': 'certificatesOfOccupancy',
	'ODDobCO': 'certificatesOfOccupancy',

	// Electrical Permits
	'App\\Models\\OpenData\\DOB\\Now\\ODDobNowElectricalPermitApplication': 'electricalPermitApplications',
	'ODDobNowElectricalPermitApplication': 'electricalPermitApplications',

	// Elevator Permits & Records
	'App\\Models\\OpenData\\DOB\\Now\\Build\\ODDobNowBuildElevatorPermitApplication': 'elevatorPermitApplications',
	'ODDobNowBuildElevatorPermitApplication': 'elevatorPermitApplications',
	'App\\Models\\BIS\\Elevator\\BISElevator': 'elevatorRecords',
	'BISElevator': 'elevatorRecords',

	// Limited Alteration Applications
	'App\\Models\\OpenData\\DOB\\Now\\Build\\ODDobNowBuildLimitedAlterationApplication': 'limitedAlterationApplications',
	'ODDobNowBuildLimitedAlterationApplication': 'limitedAlterationApplications',

	// Boilers
	'App\\Models\\OpenData\\DOB\\Now\\ODDobNowSafetyBoiler': 'dobNowSafetyBoilers',
	'ODDobNowSafetyBoiler': 'dobNowSafetyBoilers',
	'App\\Models\\BIS\\Boiler\\BISBoiler': 'bisBoilers',
	'BISBoiler': 'bisBoilers',

	// Facades
	'App\\Models\\OpenData\\DOB\\Now\\ODDobNowFacade': 'dobNowSafetyFacades',
	'ODDobNowFacade': 'dobNowSafetyFacades',

	// Signs
	'App\\Models\\BIS\\BISIlluminatedSign': 'bisIlluminatedSigns',
	'BISIlluminatedSign': 'bisIlluminatedSigns',

	// HPD
	'App\\Models\\OpenData\\HPD\\ODHPDHousingViolation': 'hpdHousingViolations',
	'ODHPDHousingViolation': 'hpdHousingViolations',
	'App\\Models\\OpenData\\HPD\\ODHPDHousingLitigation': 'housingLitigations',
	'ODHPDHousingLitigation': 'housingLitigations',
	'App\\Models\\OpenData\\HPD\\ODHPDComplaint': 'hpdComplaints',
	'ODHPDComplaint': 'hpdComplaints',
	'App\\Models\\OpenData\\HPD\\ODHPDRepairVacateOrder': 'repairVacateOrders',
	'ODHPDRepairVacateOrder': 'repairVacateOrders',

	// FDNY
	'App\\Models\\OpenData\\FDNY\\ODFDNYViolation': 'fdnyViolations',
	'ODFDNYViolation': 'fdnyViolations',
	'App\\Models\\FdnyNovRecord': 'fdnyNov',
	'FdnyNovRecord': 'fdnyNov',
	'App\\Models\\FdnyVioRecord': 'fdnyVio',
	'FdnyVioRecord': 'fdnyVio',
	'App\\Models\\FdnyPermitAccount': 'fdnyPermits2',
	'FdnyPermitAccount': 'fdnyPermits2',

	// Hearings & Standards
	'App\\Models\\OpenData\\ODOathHearing': 'fdnyOathHearings',
	'ODOathHearing': 'fdnyOathHearings',
	'App\\Models\\OpenData\\ODStandardsApplicationStatus': 'standardsApplicationStatuses',
	'ODStandardsApplicationStatus': 'standardsApplicationStatuses',

	// Sidewalk
	'App\\Models\\SideWalkViolation': 'sideWalkViolations',
	'SideWalkViolation': 'sideWalkViolations',

	// DOT Liens / County Clerk
	'App\\Models\\CountyClerk\\CountyClerkFiling': 'dotLiens',
	'CountyClerkFiling': 'dotLiens',

	// ACRIS
	'App\\Models\\ACRIS\\ACRISRPL': 'acrisLegals',
	'ACRISRPL': 'acrisLegals',
	'App\\Models\\ACRIS\\ACRISDocument': 'acrisDocuments',
	'ACRISDocument': 'acrisDocuments',

	// Other datasets
	'App\\Models\\OpenData\\DEP\\ODDEPECBViolations': 'depEcbViolations',
	'ODDEPECBViolations': 'depEcbViolations',
	'App\\Models\\TaxLienSale': 'taxLienSales',
	'TaxLienSale': 'taxLienSales',
	'App\\Models\\OpenData\\DOS\\OdSlaLiquorLicense': 'slaLiquorLicenses',
	'OdSlaLiquorLicense': 'slaLiquorLicenses',
	'App\\Models\\OpenData\\ODLandmarkViolation': 'landmarkViolations',
	'ODLandmarkViolation': 'landmarkViolations',
	'App\\Models\\OpenData\\ODLandmarkApplication': 'landmarkApplications',
	'ODLandmarkApplication': 'landmarkApplications',
	'App\\Models\\OpenData\\ODDeedRestriction': 'deedRestrictions',
	'ODDeedRestriction': 'deedRestrictions',
	'App\\Models\\CitypayTicket': 'citypayTickets',
	'CitypayTicket': 'citypayTickets',
};

/**
 * @param {string|null} className The email's dataset model class.
 * @returns {string|null} The dataConfig key, or null when the record cannot be opened in the app.
 */
export function getEmailDataSet(className) {
	if (!className) return null;
	if (DATA_SET_BY_MODEL_CLASS[className]) {
		return DATA_SET_BY_MODEL_CLASS[className];
	}
	const clean = className.replace(/^\\+/, '');
	if (DATA_SET_BY_MODEL_CLASS[clean]) {
		return DATA_SET_BY_MODEL_CLASS[clean];
	}
	const basename = clean.split('\\').pop();
	return DATA_SET_BY_MODEL_CLASS[basename] || null;
}

/**
 * Short headline for a sent email, e.g. "New ECB Violation" or "ECB Violation updated".
 *
 * @param {object} email A sent-email row.
 * @returns {string} The headline.
 */
export function getEmailHeadline(email) {
	const dataset = email?.class ? getDatasetLabel(email.class) : 'Record';
	return email?.type === 'notification' ? `${dataset} updated` : `New ${dataset}`;
}

/**
 * One line listing what changed in an update email, e.g. "Status: ACTIVE → RESOLVED".
 *
 * @param {object|null} changeData The email's change_data.
 * @returns {string} The changes, or an empty string when there are none.
 */
export function getEmailChangeSummary(changeData) {
	return getEmailChanges(changeData)
		.map(({ field, from, to }) => (from === '—' ? `${field}: ${to}` : `${field}: ${from} → ${to}`))
		.join('; ');
}
